import { useEffect, useMemo } from "react";
import type { SxProps, Theme } from "@mui/material/styles";
import { Typography } from "@mui/material";
import { useMarketplaceStore } from "../../../../store/useMarketplaceStore";
import { BaseFinancialReport } from "../../../orders/utils/types";
import { useFinancialReportForm } from "./financial/form/useFinancialReportForm";
import { FinancialRow } from "./financial/row/FinancialRow";
import { FinancialRowList } from "./financial/row/FinancialRowList";
import { PriceRowLayout } from "./financial/row/PriceRowLayout";
import { PriceField } from "./financial/row/PriceField";
import {
  FinancialReportView,
  FinancialRowConfig,
  FINANCIAL_ROWS,
  ProfitMode,
  PROFIT_MODE,
  RegisterSaveFn,
  FinancialFieldKey,
} from "./types";

type Props = {
  financialReport: BaseFinancialReport;
  actualReport?: BaseFinancialReport; // 👈 YENİ
  mode: ProfitMode;
  editMode?: boolean;
  inputSx?: SxProps<Theme>;
  onRegisterSave?: (_fn: RegisterSaveFn) => void;
  onDirtyChange?: (_dirty: boolean) => void;
};

const calculateProfit = (
  values: Partial<Record<FinancialFieldKey, string | number>>,
): number => {
  const toNumber = (v: string | number | undefined): number =>
    typeof v === "number" ? v : Number(v ?? 0);

  const result =
    toNumber(values.salesPrice) -
    toNumber(values.productCost) -
    toNumber(values.shippingCost) -
    toNumber(values.warehouseCost) -
    toNumber(values.commission) -
    toNumber(values.totalTax) +
    toNumber(values.sellerRefund) -
    toNumber(values.buyerRefund);

  return Math.round(result * 100) / 100;
};

export const FinancialReportTab = ({
  financialReport,
  actualReport,
  mode,
  editMode = false,
  inputSx,
  onRegisterSave,
  onDirtyChange,
}: Props) => {
  const selectedCountry = useMarketplaceStore((s) => s.selectedCountry);
  const rate = selectedCountry?.rate ?? 1;

  const report = useMemo<FinancialReportView>(() => {
    if (mode !== PROFIT_MODE.OVERRIDDEN || !actualReport) {
      return { ...(financialReport as FinancialReportView) };
    }

    const overridden = financialReport as FinancialReportView;
    const actual = actualReport as FinancialReportView;

    const merged: FinancialReportView = {};

    FINANCIAL_ROWS.forEach(({ key }) => {
      merged[key] = overridden[key] ?? actual[key] ?? 0;
    });

    return { ...merged };
  }, [financialReport, actualReport, mode]);

  const form = useFinancialReportForm(report, onRegisterSave);

  const dynamicProfit = useMemo(() => {
    if (!editMode) return report.profit ?? 0;

    if (!form.isDirty) return report.profit ?? 0;

    return calculateProfit(form.watched);
  }, [editMode, form.isDirty, form.watched, report.profit]);

  useEffect(() => {
    if (mode !== PROFIT_MODE.ESTIMATED) {
      onDirtyChange?.(form.isDirty);
    }
  }, [mode, form.isDirty, onDirtyChange]);

  const shouldHideRow = (row: FinancialRowConfig) => {
    if (!row.hideIfZero) return false;
    const value = report[row.key];
    return value == null || Number(value) === 0;
  };

  const rows = FINANCIAL_ROWS.filter((row) => {
    if (mode === PROFIT_MODE.ACTUAL) {
      return editMode || !shouldHideRow(row);
    }
    return !shouldHideRow(row);
  });

  return (
    <FinancialRowList
      rows={rows}
      currency={selectedCountry?.currency}
      renderRow={({ row }) => {
        const key = row.key;

        let rawValue: string | number | undefined;
        let editable = false;

        if (mode === PROFIT_MODE.ESTIMATED) {
          rawValue = report[key] ?? 0;
        } else if (key === "profit") {
          rawValue = editMode ? dynamicProfit : (report.profit ?? 0);
        } else {
          rawValue = editMode
            ? (form.watched[key] ?? report[key] ?? 0)
            : (report[key] ?? 0);

          editable = editMode;
        }

        const numeric = Number(rawValue);

        const usd = Number.isNaN(numeric) ? "-" : (numeric / rate).toFixed(2);
        if (mode === PROFIT_MODE.ESTIMATED) {
          return (
            <PriceRowLayout label={row.label} variant={row.variant} isFrozen>
              <PriceField
                value={rawValue}
                currency={selectedCountry?.currency}
                editable={false}
              />
              <Typography variant="caption">{usd} $</Typography>
            </PriceRowLayout>
          );
        }

        return (
          <FinancialRow
            row={row}
            value={numeric}
            usd={usd}
            editable={editable}
            control={form.control}
            inputSx={inputSx}
            currency={selectedCountry?.currency}
          />
        );
      }}
    />
  );
};
